import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, matchPath } from "react-router-dom";

import { AppShell } from "@/shared/ui/AppShell/index.js";
import { ComposeFab } from "@/shared/ui/ComposeFab/index.js";
import { Lightbox } from "@/shared/ui/Lightbox/index.js";
import { RouteAnnouncer } from "@/shared/ui/RouteAnnouncer/index.js";

import { useComposeStore } from "@/features/compose/composeStore.js";
import { useAuthSheetStore } from "@/features/auth/authSheetStore.js";
import { useSearchSheetStore } from "@/features/search/searchSheetStore.js";
import { apiState } from "@/shared/api/client.js";
import { useAuthStore } from "@/shared/stores/authStore.js";
import { useSettingsStore } from "@/shared/stores/useSettingsStore.js";
import { ThemeId, THEME_OPTIONS, useThemeStore } from "@/shared/stores/themeStore.js";

// Phase 4.1 — defer heavy interactive sheets until they are actually opened.
// ComposeSheetHost and DriftBottleSheet stay null when closed, but their
// modules and dependencies (TanStack Query mutations, ComposeSheet, etc.)
// used to load on every initial page hit. Lazy + conditional mount means
// users who never tap "贴纸条" or "漂流瓶" never download those chunks.
const ComposeSheetHost = lazy(() =>
  import("@/features/compose/ComposeSheetHost.js").then((mod) => ({
    default: mod.ComposeSheetHost,
  })),
);
const DriftBottleSheet = lazy(() =>
  import("@/features/drift-bottle/DriftBottleSheet.js").then((mod) => ({
    default: mod.DriftBottleSheet,
  })),
);
// audit-2026-05-18 (deep, P1 paradigm): SearchSheetHost mounts the H5
// `#searchWindow` sheet *on top* of whatever page is rendered, instead of
// navigating to a separate `/search` route. The store-driven mount keeps
// the home wall visible underneath, matching H5's `openSearchWindow()`.
const SearchSheetHost = lazy(() =>
  import("@/features/search/SearchSheetHost.js").then((mod) => ({
    default: mod.SearchSheetHost,
  })),
);

// audit-2026-05-18 (full-alignment, Phase 3.3): mirror H5 detail-overlay
// paradigm — H5 detail is always an overlay rendered on top of the active
// view (typically home/board wall stays visible behind). React's /p/:id
// previously was a stand-alone route with no backing view. We mount
// HomePage as a backing layer when the user lands directly on /p/:id;
// the detail-overlay's high z-index covers it visually. Lazy-imported so
// the chunk only ships when needed.
const HomePageBacking = lazy(() =>
  import("@/pages/HomePage.js").then((mod) => ({ default: mod.HomePage })),
);

// audit-2026-05-18 (UX-5.1): H5 main.js:6384 startComposeFlow shows
// #publishTargetWindow (a board picker sheet) BEFORE opening compose when
// the user taps compose from home/parent context. React was opening compose
// directly with default board "life", skipping target selection.
const PublishTargetSheet = lazy(() =>
  import("@/features/compose/PublishTargetSheet.js").then((mod) => ({
    default: mod.PublishTargetSheet,
  })),
);

function RouteFallback() {
  return (
    <p className="app-route-fallback" role="status" aria-live="polite">
      纸条正在准备……
    </p>
  );
}

/**
 * isComposeFabHidden — H5 hides the compose FAB on menu + utility views
 * (main.js:3735-3738). React routes mapped to those view-types:
 *   - /u/:userId    → utility (profile) — HIDE
 *   - /settings     → utility — HIDE
 *   - /governance   → utility — HIDE
 *   - /menu         → menu — HIDE
 *   - /search       → search overlay; sheet overlays home — already visible on /
 *
 * Phase 3.3 update: /p/:id is detail overlay rendered on top of HomePage
 * backing (which is "home" view-equivalent). H5 keeps compose-button
 * VISIBLE during detail overlay because the underlying home/board view
 * still owns it. Mirror by NOT hiding compose on /p/:id.
 *
 * Visible on: / (home), /b/:id (board), /p/:id (detail-overlay-on-home).
 */
function isComposeFabHidden(pathname: string): boolean {
  if (pathname === "/" || pathname.startsWith("/b/") || pathname.startsWith("/p/")) {
    return false;
  }
  return true;
}

/**
 * App — phase H minimal shell.
 *
 * H5 source: `<body><div class="device-frame"><main id="app" class="app-window">...
 * Per-view headers (.home-head / .window-bar) and the floating compose button
 * live INSIDE each window-view, NOT in a global app-bar. Phase H removes the
 * global .app-bar and .bottom-nav that earlier phases had introduced; the
 * only globally-mounted affordance is now the ComposeFab (matching H5's
 * <button id="composeButton" class="compose-button">) and the lazy sheets.
 */
export function App() {
  const { pathname } = useLocation();
  const currentUser = useAuthStore((state) => state.currentUser);
  const openAuthSheet = useAuthSheetStore((state) => state.openAuthSheet);
  const openCompose = useComposeStore((state) => state.openCompose);
  const composeOpen = useComposeStore((state) => state.isOpen);
  const searchSheetOpen = useSearchSheetStore((state) => state.isOpen);
  const [driftBottleOpen, setDriftBottleOpen] = useState(false);

  // Mirror H5 main.js:5921-5927 — keep `reduce-motion` / `compact-density` body
  // classes in sync with the persisted settings store. RULE 13 compliant:
  // we only toggle classList on the existing <body>, no extra DOM mounted.
  const reduceMotion = useSettingsStore((state) => state.reduceMotion);
  const density = useSettingsStore((state) => state.density);
  useEffect(() => {
    document.body.classList.toggle("reduce-motion", reduceMotion);
  }, [reduceMotion]);
  useEffect(() => {
    document.body.classList.toggle("compact-density", density === "compact");
  }, [density]);

  // audit-2026-05-18 (UX-5.1): match H5 startComposeFlow (main.js:6384).
  // On board page → open compose with that board locked; on home/anywhere
  // else → open PublishTargetSheet first to pick a primary target board,
  // then open compose with that board pre-selected.
  const boardPathMatch = useMemo(
    () => matchPath({ path: "/b/:boardId" }, pathname),
    [pathname],
  );
  const lockedBoardId = boardPathMatch?.params.boardId;
  const [publishTargetOpen, setPublishTargetOpen] = useState(false);

  function handleComposeClick() {
    if (!currentUser || !apiState.token) {
      openAuthSheet("login");
      return;
    }
    if (lockedBoardId) {
      openCompose({ primaryBoardId: lockedBoardId });
      return;
    }
    setPublishTargetOpen(true);
  }

  function handlePublishTargetSelect(boardId: string) {
    setPublishTargetOpen(false);
    openCompose({ primaryBoardId: boardId });
  }

  // audit-2026-05-18 (full-alignment, Phase 3.3): when deep-linking to
  // /p/:id, render Home as a backing layer so the wall is visible behind
  // the detail overlay — matches H5 behaviour where detail-overlay always
  // appears on top of the currently active view. The PostDetailPage's
  // .overlay.detail-overlay has high z-index and translucent backdrop so
  // the wall shows through, just like H5.
  const isDetailRoute = /^\/p\//.test(pathname);

  return (
    <AppShell>
      {isDetailRoute ? (
        <Suspense fallback={null}>
          <HomePageBacking />
        </Suspense>
      ) : null}
      <Suspense fallback={<RouteFallback />}>
        <Outlet context={{ onOpenDriftBottle: () => setDriftBottleOpen(true) }} />
      </Suspense>

      {/* audit-2026-05-18 (full-alignment, profile): H5 hides composeButton
          on utility / menu views (main.js:3735-3738); only home + board
          show the FAB. Mirror by hiding on /u/:id, /settings, /governance
          routes (utility-equivalent) and /menu. */}
      <ComposeFab onTrigger={handleComposeClick} hidden={isComposeFabHidden(pathname)} />

      {publishTargetOpen ? (
        <Suspense fallback={null}>
          <PublishTargetSheet
            open={publishTargetOpen}
            onClose={() => setPublishTargetOpen(false)}
            onSelect={handlePublishTargetSelect}
          />
        </Suspense>
      ) : null}
      {composeOpen ? (
        <Suspense fallback={null}>
          <ComposeSheetHost />
        </Suspense>
      ) : null}
      {searchSheetOpen ? (
        <Suspense fallback={null}>
          <SearchSheetHost />
        </Suspense>
      ) : null}
      {driftBottleOpen ? (
        <Suspense fallback={null}>
          <DriftBottleSheet
            isOpen={driftBottleOpen}
            onClose={() => setDriftBottleOpen(false)}
            onRequireAuth={() => openAuthSheet("login")}
          />
        </Suspense>
      ) : null}

      {/*
        P0-2 (logic-animation-audit-2026-05-19 §L4): singleton image
        lightbox portal. Always mounted so .is-open opacity transitions
        fire on attribute change (AP2-compliant) without an rAF dance;
        backdrop-filter is overridden to none in Lightbox.css when not
        .is-open so the GPU layer cost only kicks in while open. Logical
        state is owned by useLightboxStore — pages call openLightbox(images,
        startIndex) instead of imperatively poking DOM (replaces H5
        main.js:8853 openLightbox + module-scope `lightbox` mutable).
      */}
      <Lightbox />
      {/*
        Audit B-4 (a11y-deep-audit-2026-05-20 §3): visually-hidden
        aria-live region. Reads document.title on every route change
        so screen-reader users hear that the page actually changed.
      */}
      <RouteAnnouncer />
    </AppShell>
  );
}

export function ThemePreview({ theme }: { theme: ThemeId }) {
  const setTheme = useThemeStore((state) => state.setTheme);

  return (
    <section className="paper-panel">
      <h2>{THEME_OPTIONS.find((item) => item.id === theme)?.label ?? "默认"}墙面</h2>
      <p>纸条的颜色从 CSS 变量里来，主题通过 data-theme 切换。</p>
      <div className="button-row">
        <button className="paper-button" type="button" onClick={() => setTheme(theme)}>
          换到这个主题
        </button>
      </div>
    </section>
  );
}
